import {
	Prioritization,
	QueueClass,
	QueueView,
	SharedBarCut,
	SharedBarGroup,
} from './types';

/**
 * The view the tablet opens on. Every class, in one ranked list.
 *
 * Production asked for this directly: show all of them, in order, and let the
 * operator filter to one class when they want it. Opening on ready work only
 * was the earlier reading of the same request, and it traded a shorter list for
 * the operator having to trust a label before they could see what it hid.
 * Nothing is hidden now, so a wrong class costs a position in the list instead
 * of a job the operator never sees.
 *
 * This only works paired with `DEFAULT_PRIORITIZATION`: without readiness
 * ordering, `All` is an undifferentiated list of everything, which is the
 * screen operators complained about in the first place.
 *
 * The API also defaults to `All`, for a different reason: the other
 * grouped-reservation screens share that endpoint without sending `queue_view`
 * and have to keep listing everything.
 */
export const DEFAULT_QUEUE_VIEW: QueueView = 'All';

/**
 * How the tablet orders that view: by class rank first, so started cuts and
 * machine-unblocking work lead, blocked and contradictory rows sink, and the
 * required ship date breaks ties inside each class.
 *
 * This is what makes the `All` default readable — it is the "in order" half of
 * what Production asked for. Ship-date-only ordering stays available under
 * "Due date" for an operator working to a shipping deadline, and both choices
 * are named on the control so neither order is something you infer from a
 * switch being off.
 *
 * The API still defaults to `due_date`, so no other caller's order moves.
 */
export const DEFAULT_PRIORITIZATION: Prioritization = 'readiness';

/**
 * Operator-facing wording for the saw queue classification returned by
 * `GET material/material-reservations/grouped`. The server decides the class
 * and the reason codes; this file is the only place that decides how they read
 * on the tablet, so the same code never gets two different labels.
 */

interface ClassLabel {
	label: string;
	color: string;
}

/**
 * What each label means, and what an operator does about it, is in the tablet
 * user guide at §12.3.1 rather than on the tag. These pages run on touch
 * tablets, where a hover hint on a tag inside a card that navigates on tap is
 * unreachable: the tap opens the job. The guide is also what Production
 * exports for the SOP, so one wording serves both.
 */
export const QUEUE_CLASS_LABELS: Record<QueueClass, ClassLabel> = {
	continue_cutting: {
		label: 'Finish this cut',
		color: 'gold',
	},
	start_machine_soon: {
		label: 'Starts a machine',
		color: 'green',
	},
	standard_ready: {
		label: 'Ready to cut',
		color: 'blue',
	},
	blocked: {
		label: 'Blocked',
		color: 'default',
	},
	needs_review: {
		label: 'Needs review',
		color: 'volcano',
	},
	completed: {
		label: 'Cut',
		color: 'green',
	},
};

const REASON_LABELS: Record<string, string> = {
	// Blocking
	MATERIAL_NOT_RECEIVED: 'Material not received',
	MATERIAL_ON_HOLD: 'Material on quality hold',
	MATERIAL_UNAVAILABLE: 'No usable bar',
	JOB_ON_HOLD: 'Job on hold',
	PRODUCTION_NOT_ACCEPTED: 'Not accepted for production',
	// Needs review
	JOB_CANCELLED_WITH_PENDING_CUTS: 'Job cancelled, cuts still open',
	JOB_DELIVERED_WITH_PENDING_CUTS: 'Already delivered, cuts still open',
	PREP_COMPLETED_WITH_PENDING_CUTS: 'Material prep closed, cuts still open',
	PENDING_STATUS_WITH_NO_PIECES_LEFT: 'All pieces cut, still marked not cut',
	MISSING_RESERVATIONS: 'No reservations behind this row',
	// Warnings. Shown on the card like a blocking reason, but the work stays
	// cuttable: an unfinished kit lowers the rank, it does not stop the saw.
	KIT_STATUS_MISMATCH: 'Kit status disagrees with the staging board',
	KIT_NOT_READY: 'Kit not ready yet',
	// Priority
	KIT_READY: 'Kit ready',
	MATERIAL_AVAILABLE: 'Bar available',
	CUT_IN_PROGRESS: 'Cut in progress',
	NO_KIT_REQUIRED: 'No kit waiting',
	CUTTING_AHEAD: 'Cutting ahead of the kit',
};

/**
 * What each condition means and what the operator does about it, read as a
 * continuation of the reason label rather than a replacement for it.
 *
 * The label alone names a condition without saying whose problem it is:
 * "Already delivered, cuts still open" tells a saw operator nothing about
 * whether to cut. Production asked for that gap to be closed, so every reason
 * that stops work now ends in an instruction. Only `needs_review` and `blocked`
 * rows render these, so the ready cards that make up most of the queue keep
 * their height.
 */
const REASON_DETAILS: Record<string, string> = {
	// Needs review: two records contradict each other, so neither cutting nor
	// clearing the row is safe without a lead.
	JOB_CANCELLED_WITH_PENDING_CUTS:
		'The job was cancelled but the reservation was never closed. Do not cut it — a lead has to close it out.',
	JOB_DELIVERED_WITH_PENDING_CUTS:
		'The job already shipped but the reservation was never closed. Do not cut it — a lead has to close it out.',
	PREP_COMPLETED_WITH_PENDING_CUTS:
		'Prep was signed off while cuts were still open. Ask a lead whether the cut is still needed.',
	PENDING_STATUS_WITH_NO_PIECES_LEFT:
		'Every piece on this row is already cut, but the status was never moved off Not Cut. There is nothing to cut — a lead has to close it out.',
	MISSING_RESERVATIONS:
		'There is no bar behind this row to cut against. A lead has to rebuild it or remove it.',
	// Blocked: the records agree, and they say the cut cannot happen yet.
	MATERIAL_NOT_RECEIVED:
		'The bar has not arrived yet. Nothing to do at the saw until it does.',
	MATERIAL_ON_HOLD: 'Quality has to release the bar before it can be cut.',
	MATERIAL_UNAVAILABLE:
		'Nothing usable is left on this reservation. A lead has to supply material.',
	JOB_ON_HOLD:
		'No work should start on this job. A lead has to take it off hold.',
	PRODUCTION_NOT_ACCEPTED:
		'The job is not accepted for production yet, so it should not be cut.',
};

export const reasonLabel = (code: string): string =>
	REASON_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase();

export const reasonLabels = (codes?: string[]): string[] =>
	(codes ?? []).map(reasonLabel);

export const reasonDetail = (code: string): string | undefined =>
	REASON_DETAILS[code];

/**
 * Bar states that stop a cut, mirroring `BAR_STATUS_BLOCKING_REASONS` in
 * `material/services/saw_queue.py`.
 *
 * The queue and the detail screen get these codes from the server. The cut
 * modal works from raw reservations instead, so it derives the reason from the
 * bar status here rather than showing nothing.
 */
const BAR_STATUS_REASONS: Record<string, string> = {
	'Quality Hold': 'MATERIAL_ON_HOLD',
	Scrapped: 'MATERIAL_UNAVAILABLE',
	'Awaiting Delivery': 'MATERIAL_NOT_RECEIVED',
};

export const barBlockingReason = (status?: string | null): string | null => {
	const code = status ? BAR_STATUS_REASONS[status] : undefined;
	return code ? reasonLabel(code) : null;
};

/**
 * How one other cut on a shared bar reads: the order, then the part.
 *
 * That order because it matches the tag the operator is holding, and because
 * the order is the coarser of the two — it narrows the search before the part
 * name settles it. A reservation with no job behind it is stock, which is a
 * real answer to "who else wants this bar" and not a missing value.
 */
export const sharedBarCutLabel = (cut: SharedBarCut): string => {
	const parts = [cut.order_name, cut.part_name].filter(Boolean);
	return parts.length ? parts.join(' · ') : 'Stock cut';
};

/**
 * The chip for one shared bar on a queue card: which bar, and who else wants it.
 *
 * Production's complaint was that four cards all saying "same bar" gave them no
 * way to see which two went together. The bar's own ID is what fixes that —
 * two cards showing `ML-2291` are visibly the same pair — so it leads, and the
 * other job's name follows for the operator who is ready to act on it.
 *
 * Only one other cut is named. A bar with three jobs behind it would push the
 * chip past the width of a card on a 10.1" tablet, and the count that replaces
 * the rest is enough to send the operator into the job, where every one of them
 * is listed by name.
 */
export const sharedBarChipLabel = (group: SharedBarGroup): string => {
	const [first, ...rest] = group.other_cuts;
	const extra = rest.length ? ` +${rest.length}` : '';
	// A bar with no friendly id has nothing to match the other card on, which is
	// the whole point of leading with it. Naming the other job alone is the
	// honest version of the chip; "Same bar this bar" fills the slot with words
	// that answer nothing.
	if (!group.friendly_id) {
		return first
			? `Shares a bar with ${sharedBarCutLabel(first)}${extra}`
			: 'Shares a bar';
	}
	if (!first) return `Same bar ${group.friendly_id}`;
	return `Same bar ${group.friendly_id} · ${sharedBarCutLabel(first)}${extra}`;
};
